import Logger from '../logger'

import AuthManager from '../auth/auth-manager'
import PropertyManager from '../property-manager'
import CookieConsentManager from '@/cookie-consent-manager'
import SchemeServicingManager from '@/scheme-servicing-manager'

import Store from '../store'

import Property from '../models/property'

import multipleGuard from './multiple-guard'

/**
* Attempts to get the ext user from the gicen routes.
* @param {route} to The intended destination route.
  @param {route} from The source route.
* @returns {string | undefined}
*/
const getExtUserFromRoute = (from, to) => {
    const extUserFromQuery = to.query && to.query['ext-user'] ? to.query['ext-user'] : undefined
    const extUserFromParam = to.name === 'ExternalUserLogin' ? to.params.key ? to.params.key : from.params.key : undefined
    return extUserFromQuery ? extUserFromQuery : extUserFromParam ? extUserFromParam : undefined
}

const getAuthContextLazily = async function() {
    let cxt = Store.state.authContext

    if (!cxt || cxt.type === 'guest') {
        await AuthManager.checkSignInStatusAsync()
        cxt = Store.state.authContext
    }
    return cxt
}

const loggingCategory = 'Route Guard'

const Guards = {

    /**
     * Execute multiple guard functions in series using a dynamically generated guard function.
     *
     * @returns {Function} A guard function taking to, from and next parameters.
     */
    multiple: multipleGuard,

    /**
     * Require that a user accessing this page is a guest.
     * @param {route} _to The intended destination route (ignored).
     * @param {route} _from The source route (ignored).
     * @param {Function} next The next execution.
     */
    requireGuest: function(_to, _from, next) {
        const cxt = Store.state.authContext
        Logger.logObject(cxt, 'Ensuring that the application has a guest account.', loggingCategory)
        if (cxt && cxt.type === 'guest')
            next()
        else
            next({ name: 'Welcome' })
    },

    /**
     * Require that ReCaptcha has been verified to access this page.
     * @param {route} to The intended destination route.
     * @param {route} _from The source route (ignored).
     * @param {Function} next The next execution.
     */
    requireReCaptcha: async function(to, _from, next) {
        const verified = await AuthManager.checkReCaptchaVerificationAsync()
        Logger.logObject(verified, 'Ensuring that ReCaptcha verification is present.', loggingCategory)
        if (!verified)
            next({ name: 'ReCaptcha', params: { redirect: to } })
        else
            next()
    },

    /**
     * Require that a user accessing this page is a registered user account - If not this will show the Registration screen.
     * @param {route} to The intended destination route.
     * @param {route} _from The source route (ignored).
     * @param {Function} next The next execution.
     */
    requireUser: async function(to, _from, next) {
        const cxt = await getAuthContextLazily()
        Logger.logObject(cxt, 'Ensuring that the application has a user account.', loggingCategory)
        if (!cxt)
            next({ name: 'Welcome' })
        else if (cxt.type === 'guest')
            next({ name: 'Register', params: { redirect: to } })
        else
            next()
    },

    /**
     * Require that a user accessing this page is logged in - If not this will show the log in screen (You may want Registration instead with the requireUser guard).
     * @param {route} to The intended destination route.
     * @param {route} _from The source route (ignored).
     * @param {Function} next The next execution.
     */
    requireLoggedInUser: async function(to, _from, next) {
        const cxt = await getAuthContextLazily()
        Logger.logObject(cxt, 'Ensuring that the application has a logged in user account.', loggingCategory)
        if (!cxt)
            next({ name: 'Welcome' })
        else if (cxt.type === 'guest')
            next({ name: 'LogIn', params: { redirect: to } })
        else
            next()
    },

    /**
     * Require that a user accessing this page has an account or has verified ReCaptcha.
     * @param {route} to The intended destination route.
     * @param {route} from The source route.
     * @param {Function} next The next execution.
     */
    requireUserOrReCaptcha: async function(to, from, next) {
        const cxt = await getAuthContextLazily()
        Logger.logObject(cxt, 'Ensuring that the application has a user account or verified ReCaptcha.', loggingCategory)
        if (!cxt || cxt.type === 'guest')
            await Guards.requireReCaptcha(to, from, next)
        else
            next()
    },

    /**
     * Require that the state must have an active project before accessing the page.
     * @param {route} _to The intended destination route (ignored).
     * @param {route} _from The source route (ignored).
     * @param {Function} next The next execution.
     */
    requireCurrentProject: function(_to, _from, next) {
        const currentProject = Store.state.improvements && Store.state.improvements.currentProject
        Logger.logObject(currentProject, 'Ensuring that the application has a current project.', loggingCategory)
        if (!currentProject)
            next(false)
        else
            next()
    },

    /**
     * Require that the state must have a project object with parameters before accessing the page.
     * @param {route} _to The intended destination route (ignored).
     * @param {route} _from The source route (ignored).
     * @param {Function} next The next execution.
     */
    requireCurrentProjectParameters: function(_to, _from, next) {
        const currentProject = Store.state.improvements && Store.state.improvements.currentProject
        const allowed = currentProject && currentProject.budget && currentProject.budget > 0 && currentProject.motivation
        Logger.logObject(currentProject, 'Ensuring that the application has parameters to initialise a project.', loggingCategory)
        if (!allowed)
            next({ name: 'PlanChooser' })
        else
            next()
    },

    /**
     * Require that the state must have a selected property before accessing the page.
     * @param {route} to The intended destination route.
     * @param {route} from The source route.
     * @param {Function} next The next execution.
     */
    requireSelectedProperty: async function(to, from, next) {
        const getProperty = (params) => params && !isNaN(params.id) ? new Property(params) : null

        let selectedProperty = Store.getters.selectedProperty
        const fromStore = selectedProperty != null

        // noinspection JSUnresolvedVariable
        const params = to.query || to.params || from.query || from.params
        if (!selectedProperty) {
            Logger.logObject(params, 'Creating property from params', loggingCategory)
            selectedProperty = getProperty(params)
        }

        Logger.logObject(selectedProperty, 'Ensuring that the application has a property selected.', loggingCategory)

        // noinspection JSUnresolvedVariable
        const destName = to.name
        if (!selectedProperty) {
            next(false)
        } else if (!selectedProperty.isServedByScheme() && destName !== 'MyHome') {
            next({ name: 'MyHome', query: { id: selectedProperty.id } })
        } else {
            try {
                if (!fromStore)
                    await PropertyManager.choosePropertyAsync(selectedProperty)
                else
                    await PropertyManager.refreshPropertyAsync()
            } catch (err) {
                Logger.error(err, loggingCategory)
                throw err
            }

            next()
        }
    },

    /**
     * Direct to PlanNotesForm screen if the scheme doesn't offer any services.
     * @param {route} _to The intended destination route.
     * @param {route} _from The source route.
     * @param {Function} next The next execution.
     */
    requireSchemeServicesOffering: async function(_to, _from, next) {
        const scheme = await SchemeServicingManager.getActiveScheme()

        Logger.logObject(scheme, 'Ensuring that the scheme offers additional services.', loggingCategory)

        if (!scheme.services || scheme.services.length === 0)
            next({ name: 'PlanSummaryReview' })
        else
            next()
    },

    /**
     * Direct to PlanSummaryReview screen if the scheme is lite.
     * @param {route} _to The intended destination route.
     * @param {route} _from The source route.
     * @param {Function} next The next execution.
     */
    requireNonLiteScheme: async function(_to, _from, next) {
        const scheme = await SchemeServicingManager.getPresentationSchemeAsync()

        Logger.logObject(scheme, 'Ensuring that the scheme is not lite.', loggingCategory)

        if (scheme.userOnlyTool)
            next({ name: 'PlanSummaryReview' })
        else
            next()
    },

    /**
     * Prevent access to route on production environment.
     * @param {route} to The intended destination route.
     * @param {route} from The source route.
     * @param {Function} next The next execution.
     */
    requireNonProductionEnvironment: function(to, from, next) {
        if (process.env.NODE_ENV === 'production')
            next({ name: 'NotFound' })
        else
            next()
    },

    /**
     * Prevent access to route if cookie consent has not been set.
     * @param {route} to The intended destination route.
     * @param {route} from The source route.
     * @param {Function} next The next execution.
     */
    requireCookieConsent: async function(to, from, next) {
        Logger.logObject(to, 'ROUTE GUARD', loggingCategory)
        const hasSetConsent = await CookieConsentManager.hasSetConsentAsync()
        const cookieConsentRoute = 'CookieConsent'
        const extUser = getExtUserFromRoute(from, to)

        if (hasSetConsent) {
            if (extUser && to.name === 'Welcome')
                next({ name: 'ExternalUserLogin', params: { key: extUser } })
            else
                next()
        } else {
            if (extUser)
                next({ name: cookieConsentRoute, params: { redirect: to, key: extUser }, query: { 'ext-user': extUser } })
            else
                next({ name: cookieConsentRoute, params: { redirect: to } })
        }
    },

    /**
     * Prevent access to route if the current auth context is 'External'.
     * @param {route} to The intended destination route.
     * @param {route} _from The source route.
     * @param {Function} next The next execution.
     */
    disallowExternal: async function(to, _from, next) {

        Logger.logObject(to, 'ROUTE GUARD', loggingCategory)
        const cxt = await getAuthContextLazily()

        if (cxt && cxt.type === 'external')
            next({ name: 'NotFound' })
        else
            next()
    },
}

export default Guards

export { multipleGuard }
